/**
 * Pulse Bundle Offer
 * Mobile-first bundle selector integration with product form
 * Supports multipack (quantity) and add-ons (additional products)
 * Minimal JS with event delegation
 */

(function() {
  'use strict';

  if (!document.querySelector('[data-pulse-bundle]')) {
    return;
  }

  class PulseBundleOffer {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.blockId = container.dataset.blockId;
      this.bundleMode = container.dataset.pulseBundleMode || 'multipack';
      this.applyOnAddToCart = container.dataset.applyOnAddToCart === 'true';
      this.applyDiscountCode = container.dataset.applyDiscountCode === 'true';
      this.discountCode = container.dataset.discountCode || '';
      
      this.productForm = this.findProductForm();
      this.qtyInput = this.findQuantityInput();
      this.variantInput = this.findVariantInput();
      
      this.selectedMultipackQty = null;
      this.selectedAddons = [];
      this.selectedBundleVariants = {}; // Store selected variants for each bundle item
      this.productVariants = null; // Store product variants data
      
      this.init();
    }

    findProductForm() {
      const formId = `product-form-${this.sectionId}`;
      let form = document.querySelector(`#${formId}`);
      if (!form) {
        form = this.container.closest('form[action*="/cart/add"]');
      }
      if (!form) {
        form = document.querySelector('form[action*="/cart/add"]');
      }
      return form;
    }

    findQuantityInput() {
      if (!this.productForm) return null;
      return this.productForm.querySelector('[name="quantity"]');
    }

    findVariantInput() {
      if (!this.productForm) return null;
      return this.productForm.querySelector('[name="id"]');
    }

    init() {
      if (this.bundleMode === 'multipack') {
        this.initMultipack();
      } else {
        this.initAddons();
      }

      // Listen for variant changes
      this.handleVariantChange();

      // Intercept form submit if add-ons mode or multipack with variants
      // Only intercept if we actually need to (bundle selected or add-ons mode)
      if (this.applyOnAddToCart && this.productForm) {
        this.interceptFormSubmit();
      }
      
      // Ensure form works normally when no bundle is selected
      // This is handled by not preventing default in interceptFormSubmit when no bundle is selected

      // Initialize estimated total for add-ons
      if (this.bundleMode === 'addons') {
        this.updateEstimatedTotal();
      }
    }

    initMultipack() {
      // Load product variants data
      this.loadProductVariants();

      // Handle radio inputs
      const radios = this.container.querySelectorAll('input[type="radio"].pulse-bundle-offer__input');
      radios.forEach(radio => {
        radio.addEventListener('change', this.handleMultipackChange.bind(this));
        if (radio.checked) {
          // Delay to ensure DOM is ready
          setTimeout(() => {
            this.handleMultipackChange({ target: radio });
          }, 100);
        }
      });

      // Handle pill buttons (horizontal layout)
      const pills = this.container.querySelectorAll('.pulse-bundle-offer__pill');
      pills.forEach(pill => {
        pill.addEventListener('click', this.handleMultipackPillClick.bind(this));
        if (pill.classList.contains('pulse-bundle-offer__pill--selected')) {
          const qty = parseInt(pill.dataset.qty, 10);
          this.selectedMultipackQty = qty;
          const optionIndex = pill.dataset.optionIndex;
          if (optionIndex && qty > 1) {
            this.showVariantSelectors(pill);
            // Initialize price calculation
            setTimeout(() => {
              this.updateBundlePrice(optionIndex);
            }, 100);
          }
        }
      });

      // Handle variant selector changes
      this.initVariantSelectors();
    }

    loadProductVariants() {
      const variantsDataEl = this.container.querySelector('.pulse-bundle-variants-data');
      if (variantsDataEl) {
        try {
          this.productVariants = JSON.parse(variantsDataEl.textContent);
        } catch (e) {
          console.error('Error parsing product variants:', e);
        }
      }
    }

    initVariantSelectors() {
      const variantSelectors = this.container.querySelectorAll('.pulse-bundle-offer__variant-dropdown');
      variantSelectors.forEach(select => {
        select.addEventListener('change', this.handleVariantSelectorChange.bind(this));
      });
    }

    showVariantSelectors(selectedElement) {
      const optionIndex = selectedElement.dataset.optionIndex;
      if (!optionIndex) {
        // Try to get from parent label (for vertical layout)
        const label = selectedElement.closest('label');
        if (label) {
          const selector = label.querySelector('.pulse-bundle-offer__variant-selectors');
          if (selector) {
            const foundOptionIndex = selector.dataset.optionIndex;
            if (foundOptionIndex) {
              this.showVariantSelectorsForOption(foundOptionIndex);
              return;
            }
          }
        }
        // Try to find selector after the option element (outside label)
        const option = selectedElement.closest('.pulse-bundle-offer__option');
        if (option) {
          const selector = option.nextElementSibling;
          if (selector && selector.classList.contains('pulse-bundle-offer__variant-selectors')) {
            const foundOptionIndex = selector.dataset.optionIndex;
            if (foundOptionIndex) {
              this.showVariantSelectorsForOption(foundOptionIndex);
              return;
            }
          }
        }
        return;
      }

      this.showVariantSelectorsForOption(optionIndex);
    }

    hideVariantSelectors() {
      const allSelectors = this.container.querySelectorAll('.pulse-bundle-offer__variant-selectors');
      allSelectors.forEach(selector => {
        selector.style.display = 'none';
        selector.style.visibility = 'hidden';
      });
    }

    getCurrentVariantId(optionIndex, itemIndex) {
      // Get the currently selected variant based on dropdown selections
      const selector = this.container.querySelector(`.pulse-bundle-offer__variant-selectors[data-option-index="${optionIndex}"]`);
      if (!selector) {
        // Fallback: use main product variant
        return this.variantInput ? parseInt(this.variantInput.value, 10) : null;
      }

      const itemSelector = selector.querySelector(`.pulse-bundle-offer__variant-selector-item:nth-child(${itemIndex})`);
      if (!itemSelector) {
        return this.variantInput ? parseInt(this.variantInput.value, 10) : null;
      }

      const selects = itemSelector.querySelectorAll('.pulse-bundle-offer__variant-dropdown');
      if (!selects || selects.length === 0) {
        return this.variantInput ? parseInt(this.variantInput.value, 10) : null;
      }

      const selectedOptions = {};
      selects.forEach(select => {
        const optionPosition = parseInt(select.dataset.optionPosition, 10);
        const value = select.value;
        if (optionPosition === 1) {
          selectedOptions.option1 = value;
        } else if (optionPosition === 2) {
          selectedOptions.option2 = value;
        } else if (optionPosition === 3) {
          selectedOptions.option3 = value;
        }
      });

      // Find matching variant
      if (this.productVariants) {
        const variant = this.productVariants.find(v => {
          const match1 = v.option1 === selectedOptions.option1;
          const match2 = !v.option2 || v.option2 === selectedOptions.option2;
          const match3 = !v.option3 || v.option3 === selectedOptions.option3;
          return match1 && match2 && match3 && v.available;
        });
        return variant ? variant.id : (this.variantInput ? parseInt(this.variantInput.value, 10) : null);
      }

      return this.variantInput ? parseInt(this.variantInput.value, 10) : null;
    }

    handleVariantSelectorChange(e) {
      const select = e.target;
      const optionIndex = select.dataset.bundleOptionIndex;
      const itemIndex = parseInt(select.dataset.itemIndex, 10);

      if (!optionIndex || !itemIndex) return;

      // Update selected variant for this item
      if (!this.selectedBundleVariants[optionIndex]) {
        this.selectedBundleVariants[optionIndex] = {};
      }
      this.selectedBundleVariants[optionIndex][itemIndex] = this.getCurrentVariantId(optionIndex, itemIndex);

      // Update price if this bundle option is selected
      if (this.selectedMultipackQty && this.container.querySelector(`[data-option-index="${optionIndex}"]`)) {
        this.updateBundlePrice(optionIndex);
      }
    }

    updateBundlePrice(optionIndex) {
      if (!this.productVariants) return;

      const qty = parseInt(this.container.querySelector(`.pulse-bundle-offer__variant-selectors[data-option-index="${optionIndex}"]`)?.dataset.bundleQty, 10);
      if (!qty) return;

      // Calculate total price based on selected variants
      let originalTotalPrice = 0;
      for (let i = 1; i <= qty; i++) {
        const variantId = this.selectedBundleVariants[optionIndex]?.[i];
        if (variantId) {
          const variant = this.productVariants.find(v => v.id === variantId);
          if (variant && variant.available) {
            originalTotalPrice += variant.price;
          } else {
            // If variant not found or not available, use fallback
            const fallbackVariantId = this.variantInput ? parseInt(this.variantInput.value, 10) : null;
            if (fallbackVariantId) {
              const fallbackVariant = this.productVariants.find(v => v.id === fallbackVariantId);
              if (fallbackVariant) {
                originalTotalPrice += fallbackVariant.price;
              }
            }
          }
        } else {
          // If no variant selected, use current main variant
          const fallbackVariantId = this.variantInput ? parseInt(this.variantInput.value, 10) : null;
          if (fallbackVariantId) {
            const fallbackVariant = this.productVariants.find(v => v.id === fallbackVariantId);
            if (fallbackVariant) {
              originalTotalPrice += fallbackVariant.price;
            }
          }
        }
      }

      // Get discount settings
      const pill = this.container.querySelector(`.pulse-bundle-offer__pill[data-option-index="${optionIndex}"]`);
      const radio = this.container.querySelector(`input[type="radio"][value="${qty}"]`);
      const saveType = (pill?.dataset.saveType || radio?.dataset.saveType) || 'none';
      const saveValue = parseFloat(pill?.dataset.saveValue || radio?.dataset.saveValue || 0);

      // Calculate discounted price
      let discountedPrice = originalTotalPrice;
      if (saveType === 'percent' && saveValue > 0) {
        const discountMultiplier = (100 - saveValue) / 100.0;
        discountedPrice = Math.round(originalTotalPrice * discountMultiplier);
      } else if (saveType === 'amount' && saveValue > 0) {
        discountedPrice = Math.round(originalTotalPrice - (saveValue * 100));
      }

      // Update price display
      const priceEl = this.container.querySelector(`[data-bundle-price="${optionIndex}"]`);
      if (priceEl) {
        priceEl.textContent = this.formatMoney(discountedPrice);
      }

      // Update original price display (show only if there's a discount)
      const originalPriceEl = this.container.querySelector(`[data-bundle-original-price="${optionIndex}"]`);
      if (originalPriceEl) {
        if (originalTotalPrice > discountedPrice) {
          originalPriceEl.textContent = this.formatMoney(originalTotalPrice);
          originalPriceEl.style.display = '';
        } else {
          originalPriceEl.style.display = 'none';
        }
      }
    }

    handleMultipackChange(e) {
      const qty = parseInt(e.target.value, 10);
      this.selectedMultipackQty = qty;
      this.updateQuantity(qty);
      this.updatePillStates(e.target);
      
      // Show/hide variant selectors
      if (qty > 1) {
        const optionIndex = this.getOptionIndexForQty(qty);
        if (optionIndex) {
          this.showVariantSelectorsForOption(optionIndex);
        } else {
          // Try to find from label or after option element
          const label = this.container.querySelector(`label[for="${e.target.id}"]`);
          let selector = null;
          
          if (label) {
            selector = label.querySelector('.pulse-bundle-offer__variant-selectors');
          }
          
          // If not found in label, try inside option-left
          if (!selector) {
            const option = e.target.closest('.pulse-bundle-offer__option');
            if (option) {
              const optionLeft = option.querySelector('.pulse-bundle-offer__option-left');
              if (optionLeft) {
                selector = optionLeft.querySelector('.pulse-bundle-offer__variant-selectors');
              }
            }
          }
          
          if (selector && selector.dataset.optionIndex) {
            this.showVariantSelectorsForOption(selector.dataset.optionIndex);
          } else {
            // Last resort: find all selectors and try to match by qty
            const allSelectors = this.container.querySelectorAll('.pulse-bundle-offer__variant-selectors');
            allSelectors.forEach(sel => {
              const selQty = parseInt(sel.dataset.bundleQty, 10);
              if (selQty === qty && sel.dataset.optionIndex) {
                this.showVariantSelectorsForOption(sel.dataset.optionIndex);
              }
            });
          }
        }
      } else {
        this.hideVariantSelectors();
      }
    }

    getOptionIndexForQty(qty) {
      // Find the option index for a given quantity
      const radio = this.container.querySelector(`input[type="radio"][value="${qty}"]`);
      if (radio) {
        // First try to find selector inside label
        const label = this.container.querySelector(`label[for="${radio.id}"]`);
        if (label) {
          const selector = label.querySelector('.pulse-bundle-offer__variant-selectors');
          if (selector) {
            return selector.dataset.optionIndex;
          }
        }
        // If not found, try to find selector inside option-left
        const option = radio.closest('.pulse-bundle-offer__option');
        if (option) {
          const optionLeft = option.querySelector('.pulse-bundle-offer__option-left');
          if (optionLeft) {
            const selector = optionLeft.querySelector('.pulse-bundle-offer__variant-selectors');
            if (selector) {
              return selector.dataset.optionIndex;
            }
          }
        }
      }
      return null;
    }

    showVariantSelectorsForOption(optionIndex) {
      // Hide all variant selectors
      this.hideVariantSelectors();
      
      // Show variant selectors for selected option - try multiple ways to find it
      let selector = this.container.querySelector(`.pulse-bundle-offer__variant-selectors[data-option-index="${optionIndex}"]`);
      
      // If not found, try with string comparison
      if (!selector) {
        const allSelectors = this.container.querySelectorAll('.pulse-bundle-offer__variant-selectors');
        allSelectors.forEach(sel => {
          const selOptionIndex = sel.dataset.optionIndex;
          if (selOptionIndex === String(optionIndex) || selOptionIndex === optionIndex) {
            selector = sel;
          }
        });
      }
      
      if (selector) {
        const qty = parseInt(selector.dataset.bundleQty, 10);
        if (qty > 1) {
          selector.style.display = 'block';
          selector.style.visibility = 'visible';
          selector.removeAttribute('hidden');
          // Initialize selected variants for this bundle option
          if (!this.selectedBundleVariants[optionIndex]) {
            this.selectedBundleVariants[optionIndex] = {};
          }
          // Set default variants for each item (use current main variant as default)
          const defaultVariantId = this.variantInput ? parseInt(this.variantInput.value, 10) : null;
          for (let i = 1; i <= qty; i++) {
            if (!this.selectedBundleVariants[optionIndex][i]) {
              // Try to get from dropdowns, fallback to default variant
              const variantId = this.getCurrentVariantId(optionIndex, i);
              this.selectedBundleVariants[optionIndex][i] = variantId || defaultVariantId;
            }
          }
          // Update price after showing selectors (with delay to ensure DOM is ready)
          setTimeout(() => {
            this.updateBundlePrice(optionIndex);
          }, 50);
        }
      }
    }

    handleMultipackPillClick(e) {
      const pill = e.currentTarget;
      const qty = parseInt(pill.dataset.qty, 10);
      
      const isSelected = pill.classList.contains('pulse-bundle-offer__pill--selected');
      
      // Deselect all pills
      const allPills = this.container.querySelectorAll('.pulse-bundle-offer__pill');
      allPills.forEach(p => {
        p.classList.remove('pulse-bundle-offer__pill--selected');
        p.setAttribute('aria-pressed', 'false');
      });

      // Select clicked pill
      if (!isSelected) {
        pill.classList.add('pulse-bundle-offer__pill--selected');
        pill.setAttribute('aria-pressed', 'true');
        this.selectedMultipackQty = qty;
        this.updateQuantity(qty);
        
        // Show variant selectors if qty > 1
        if (qty > 1) {
          this.showVariantSelectors(pill);
        } else {
          this.hideVariantSelectors();
        }
      } else {
        this.selectedMultipackQty = null;
        this.updateQuantity(1);
        this.hideVariantSelectors();
      }

      // Update radio buttons if they exist
      const radios = this.container.querySelectorAll('input[type="radio"].pulse-bundle-offer__input');
      radios.forEach(radio => {
        if (parseInt(radio.value, 10) === qty && !isSelected) {
          radio.checked = true;
        } else if (isSelected) {
          radio.checked = false;
        }
      });
    }

    updatePillStates(selectedRadio) {
      const qty = parseInt(selectedRadio.value, 10);
      const pills = this.container.querySelectorAll('.pulse-bundle-offer__pill');
      pills.forEach(pill => {
        const pillQty = parseInt(pill.dataset.qty, 10);
        if (pillQty === qty) {
          pill.classList.add('pulse-bundle-offer__pill--selected');
          pill.setAttribute('aria-pressed', 'true');
        } else {
          pill.classList.remove('pulse-bundle-offer__pill--selected');
          pill.setAttribute('aria-pressed', 'false');
        }
      });
    }

    updateQuantity(qty) {
      // Ensure quantity input exists - inject if needed
      if (!this.qtyInput && this.productForm) {
        this.qtyInput = this.productForm.querySelector('[name="quantity"]');
        if (!this.qtyInput) {
          // Inject hidden quantity input into the form
          this.qtyInput = document.createElement('input');
          this.qtyInput.type = 'hidden';
          this.qtyInput.name = 'quantity';
          this.qtyInput.setAttribute('data-pulse-injected-qty', 'true');
          this.productForm.appendChild(this.qtyInput);
        }
      }

      if (!this.qtyInput) return;

      this.qtyInput.value = qty;

      // Trigger events for any listeners
      const inputEvent = new Event('input', { bubbles: true });
      this.qtyInput.dispatchEvent(inputEvent);

      const changeEvent = new Event('change', { bubbles: true });
      this.qtyInput.dispatchEvent(changeEvent);
    }

    initAddons() {
      const checkboxes = this.container.querySelectorAll('.pulse-bundle-offer__addon-checkbox');
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', this.handleAddonChange.bind(this));
        if (checkbox.checked) {
          this.handleAddonChange({ target: checkbox });
        }
      });

      // Secondary add button
      const secondaryButton = this.container.querySelector('[data-secondary-add-button]');
      if (secondaryButton) {
        secondaryButton.addEventListener('click', this.handleSecondaryAddClick.bind(this));
      }
    }

    handleAddonChange(e) {
      const checkbox = e.target;
      const variantId = parseInt(checkbox.dataset.variantId, 10);

      if (checkbox.checked) {
        if (!this.selectedAddons.includes(variantId)) {
          this.selectedAddons.push(variantId);
        }
      } else {
        this.selectedAddons = this.selectedAddons.filter(id => id !== variantId);
      }

      this.updateEstimatedTotal();
    }

    updateEstimatedTotal() {
      const estimatedTotalEl = this.container.querySelector('[data-estimated-total]');
      if (!estimatedTotalEl) return;

      const valueEl = estimatedTotalEl.querySelector('[data-estimated-value]');
      if (!valueEl) return;

      // Get main product price
      const priceEl = document.querySelector(`#price-${this.sectionId}`);
      if (!priceEl) return;

      let totalPrice = 0;

      // Get main variant price (in cents from data attribute if available, otherwise parse from text)
      let mainPriceCents = 0;
      const mainPriceData = priceEl.querySelector('[data-price]');
      if (mainPriceData) {
        mainPriceCents = parseInt(mainPriceData.dataset.price, 10) || 0;
      } else {
        // Fallback: parse from text (assumes displayed price is in dollars)
        const mainVariantPriceText = priceEl.textContent || '';
        const mainPriceMatch = mainVariantPriceText.match(/[\d,]+\.?\d*/);
        if (mainPriceMatch) {
          const mainPriceStr = mainPriceMatch[0].replace(/,/g, '');
          mainPriceCents = Math.round(parseFloat(mainPriceStr) * 100) || 0;
        }
      }
      totalPrice = mainPriceCents;

      // Add add-on prices (already in cents from Liquid)
      const checkedAddons = this.container.querySelectorAll('.pulse-bundle-offer__addon-checkbox:checked');
      checkedAddons.forEach(checkbox => {
        const priceEl = checkbox.closest('.pulse-bundle-offer__addon-item').querySelector('.pulse-bundle-offer__addon-price');
        if (priceEl) {
          // Try to get from data attribute first
          const priceData = priceEl.dataset.price;
          if (priceData) {
            totalPrice += parseInt(priceData, 10) || 0;
          } else {
            // Fallback: parse from text
            const priceText = priceEl.textContent || '';
            const priceMatch = priceText.match(/[\d,]+\.?\d*/);
            if (priceMatch) {
              const priceStr = priceMatch[0].replace(/,/g, '');
              totalPrice += Math.round(parseFloat(priceStr) * 100) || 0;
            }
          }
        }
      });

      // Format and display
      valueEl.textContent = this.formatMoney(totalPrice);
    }

    formatMoney(amount) {
      // Simple money formatting - just return the formatted number
      // Don't try to extract format from existing price to avoid duplicating text
      if (typeof amount !== 'number') {
        amount = parseFloat(amount) || 0;
      }
      
      // Use Shopify.formatMoney with simple format to avoid adding "Regular price" or "Sale price" text
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        // Use {{amount}} format to get only the formatted number without labels
        return Shopify.formatMoney(amount, '{{amount}}');
      }
      
      // Fallback: basic formatting
      const formatted = (amount / 100).toFixed(2);
      return '$' + formatted;
    }

    interceptFormSubmit() {
      if (!this.productForm) return;

      const productFormElement = this.productForm.closest('product-form');
      if (!productFormElement) return;

      // For simple multipack (same variant, quantity N): just update quantity in form
      // Let product-form.js handle the submit normally - this is safer and more reliable
      // Only intercept for complex cases (multiple variants or add-ons)
      
      // Update quantity BEFORE form submits (in capture phase, before product-form handler)
      this.productForm.addEventListener('submit', (e) => {
        // Ensure quantity is updated before form submits (for simple multipack)
        if (this.bundleMode === 'multipack' && this.selectedMultipackQty && this.selectedMultipackQty > 1) {
          const optionIndex = this.getSelectedOptionIndex();
          
          // Only intercept if we have different variants selected
          if (optionIndex && this.selectedBundleVariants[optionIndex] && Object.keys(this.selectedBundleVariants[optionIndex]).length > 0) {
            // Multiple different variants - must intercept
            e.preventDefault();
            e.stopPropagation();
            this.handleAddToCartWithBundleVariants();
            return;
          }
          // For simple multipack (same variant), just ensure quantity is set
          // Don't intercept - let product-form.js handle it
          // Update quantity immediately before submit
          this.updateQuantity(this.selectedMultipackQty);
        }

        // Check for add-ons - must intercept
        if (this.bundleMode === 'addons' && this.selectedAddons.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          this.handleAddToCartWithAddons();
          return;
        }
      }, true);
    }

    getSelectedOptionIndex() {
      // Get the option index for the currently selected bundle
      if (this.selectedMultipackQty) {
        const selectedPill = this.container.querySelector('.pulse-bundle-offer__pill--selected');
        if (selectedPill && selectedPill.dataset.optionIndex) {
          return selectedPill.dataset.optionIndex;
        }
        const selectedRadio = this.container.querySelector('input[type="radio"].pulse-bundle-offer__input:checked');
        if (selectedRadio) {
          // Try to find selector inside label
          const label = this.container.querySelector(`label[for="${selectedRadio.id}"]`);
          if (label) {
            const selector = label.querySelector('.pulse-bundle-offer__variant-selectors');
            if (selector) {
              return selector.dataset.optionIndex;
            }
          }
          // Try to find selector inside option-left
          const option = selectedRadio.closest('.pulse-bundle-offer__option');
          if (option) {
            const optionLeft = option.querySelector('.pulse-bundle-offer__option-left');
            if (optionLeft) {
              const selector = optionLeft.querySelector('.pulse-bundle-offer__variant-selectors');
              if (selector) {
                return selector.dataset.optionIndex;
              }
            }
          }
        }
      }
      return null;
    }

    /**
     * Handle add to cart for simple multipack (same variant, quantity N)
     * NOTE: This method is no longer used for simple multipack.
     * We now just update the quantity in the form and let product-form.js handle the submit.
     * This method is kept only for fallback scenarios.
     */
    async handleAddToCartWithMultipack() {
      // This should not be called for simple multipack anymore
      // Just ensure quantity is set and let form submit normally
      if (this.selectedMultipackQty && this.selectedMultipackQty > 1) {
        this.updateQuantity(this.selectedMultipackQty);
      }
    }

    /**
     * Handle add to cart for multipack with different variants selected
     * NOTE: Bundle discount shown in UI is display-only unless merchant configures
     * real discounts via Shopify automatic discounts, Functions, or apps.
     */
    async handleAddToCartWithBundleVariants() {
      if (!this.variantInput) return;

      const optionIndex = this.getSelectedOptionIndex();
      if (!optionIndex || !this.selectedBundleVariants[optionIndex]) {
        // Fallback to simple multipack
        this.handleAddToCartWithMultipack();
        return;
      }

      // Build items array with selected variants
      const items = [];
      const qty = this.selectedMultipackQty;
      
      for (let i = 1; i <= qty; i++) {
        const variantId = this.selectedBundleVariants[optionIndex][i];
        if (variantId) {
          // Ensure variantId is a number
          const id = parseInt(variantId, 10);
          if (id && !isNaN(id)) {
            items.push({
              id: id,
              quantity: 1
            });
          }
        }
      }

      if (items.length === 0) {
        // No valid variants, fallback to simple multipack
        this.handleAddToCartWithMultipack();
        return;
      }

      // Get cart element
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const submitButton = this.productForm.querySelector('[type="submit"]');

      if (submitButton) {
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading');
        const loadingOverlay = this.productForm.querySelector('.loading-overlay__spinner');
        if (loadingOverlay) {
          loadingOverlay.classList.remove('hidden');
        }
      }

      try {
        // Use JSON format (standard Shopify method for multiple items)
        const bodyData = {
          items: items
        };

        if (cart) {
          const sectionsToRender = cart.getSectionsToRender ? cart.getSectionsToRender() : [];
          if (sectionsToRender.length > 0) {
            // Map sections - use section.section if available, otherwise section.id
            const sections = sectionsToRender.map(s => s.section || s.id).filter(Boolean);
            if (sections.length > 0) {
              bodyData.sections = sections.join(',');
              bodyData.sections_url = window.location.pathname;
            }
          }
          cart.setActiveElement && cart.setActiveElement(document.activeElement);
        }

        const response = await fetch((typeof routes !== 'undefined' && routes.cart_add_url) || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(bodyData)
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ status: 422, description: 'Failed to add items to cart' }));
          this.handleError(errorData.description || errorData.message || 'An error occurred. Please try again.');
          return;
        }

        const data = await response.json();

        if (data.status) {
          // Error
          this.handleError(data.description || data.message);
          return;
        }

        // Success - dispatch custom event
        const event = new CustomEvent('pulse:cart:added', {
          bubbles: true,
          detail: { items, response: data }
        });
        document.dispatchEvent(event);

        // Publish standard Shopify cart update event
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pulse-bundle-offer' });
        }

        // Let cart drawer handle rendering
        if (cart && cart.renderContents) {
          cart.renderContents(data);
          // If discount code is configured and cart drawer exists, redirect to checkout
          if (this.applyDiscountCode && this.discountCode) {
            setTimeout(() => {
              window.location = ((typeof routes !== 'undefined' && routes.checkout_url) || '/checkout') + '?discount=' + encodeURIComponent(this.discountCode);
            }, 500);
          }
        } else if (!cart) {
          // No cart drawer - redirect to cart or checkout with discount
          if (this.applyDiscountCode && this.discountCode) {
            window.location = ((typeof routes !== 'undefined' && routes.checkout_url) || '/checkout') + '?discount=' + encodeURIComponent(this.discountCode);
          } else {
            window.location = (typeof routes !== 'undefined' && routes.cart_url) || '/cart';
          }
        }
      } catch (error) {
        console.error('Error adding bundle to cart:', error);
        this.handleError('An error occurred. Please try again.');
      } finally {
        if (submitButton) {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
          const loadingOverlay = this.productForm.querySelector('.loading-overlay__spinner');
          if (loadingOverlay) {
            loadingOverlay.classList.add('hidden');
          }
        }
      }
    }

    async handleAddToCartWithAddons() {
      if (!this.variantInput || !this.qtyInput) return;

      const mainVariantId = parseInt(this.variantInput.value, 10);
      const mainQty = parseInt(this.qtyInput.value, 10) || 1;

      // Build items array - ensure IDs are numbers
      const items = [];
      if (mainVariantId && !isNaN(mainVariantId)) {
        items.push({
          id: parseInt(mainVariantId, 10),
          quantity: parseInt(mainQty, 10) || 1
        });
      }

      // Add add-ons - ensure IDs are numbers
      this.selectedAddons.forEach(variantId => {
        const id = parseInt(variantId, 10);
        if (id && !isNaN(id)) {
          items.push({
            id: id,
            quantity: 1
          });
        }
      });

      if (items.length === 0) {
        this.handleError('No valid items to add to cart');
        return;
      }

      // Get cart element
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const submitButton = this.productForm.querySelector('[type="submit"]');

      if (submitButton) {
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading');
        const loadingOverlay = this.productForm.querySelector('.loading-overlay__spinner');
        if (loadingOverlay) {
          loadingOverlay.classList.remove('hidden');
        }
      }

      try {
        // Use JSON format (standard Shopify method for multiple items)
        const bodyData = {
          items: items
        };

        if (cart) {
          const sectionsToRender = cart.getSectionsToRender ? cart.getSectionsToRender() : [];
          if (sectionsToRender.length > 0) {
            // Map sections - use section.section if available, otherwise section.id
            const sections = sectionsToRender.map(s => s.section || s.id).filter(Boolean);
            if (sections.length > 0) {
              bodyData.sections = sections.join(',');
              bodyData.sections_url = window.location.pathname;
            }
          }
          cart.setActiveElement && cart.setActiveElement(document.activeElement);
        }

        const response = await fetch((typeof routes !== 'undefined' && routes.cart_add_url) || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(bodyData)
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ status: 422, description: 'Failed to add items to cart' }));
          this.handleError(errorData.description || errorData.message || 'An error occurred. Please try again.');
          return;
        }

        const data = await response.json();

        if (data.status) {
          // Error
          this.handleError(data.description || data.message);
          return;
        }

        // Success - dispatch custom event
        const event = new CustomEvent('pulse:cart:added', {
          bubbles: true,
          detail: { items, response: data }
        });
        document.dispatchEvent(event);

        // Publish standard Shopify cart update event
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pulse-bundle-offer' });
        }

        // Let cart drawer handle rendering
        if (cart && cart.renderContents) {
          cart.renderContents(data);
          // If discount code is configured and cart drawer exists, redirect to checkout
          if (this.applyDiscountCode && this.discountCode) {
            setTimeout(() => {
              window.location = ((typeof routes !== 'undefined' && routes.checkout_url) || '/checkout') + '?discount=' + encodeURIComponent(this.discountCode);
            }, 500);
          }
        } else if (!cart) {
          // No cart drawer - redirect to cart or checkout with discount
          if (this.applyDiscountCode && this.discountCode) {
            window.location = ((typeof routes !== 'undefined' && routes.checkout_url) || '/checkout') + '?discount=' + encodeURIComponent(this.discountCode);
          } else {
            window.location = (typeof routes !== 'undefined' && routes.cart_url) || '/cart';
          }
        }
      } catch (error) {
        console.error('Error adding bundle to cart:', error);
        this.handleError('An error occurred. Please try again.');
      } finally {
        if (submitButton) {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
          const loadingOverlay = this.productForm.querySelector('.loading-overlay__spinner');
          if (loadingOverlay) {
            loadingOverlay.classList.add('hidden');
          }
        }
      }
    }

    handleError(message) {
      const errorWrapper = this.productForm?.querySelector('.product-form__error-message-wrapper');
      if (errorWrapper) {
        const errorMessage = errorWrapper.querySelector('.product-form__error-message');
        if (errorMessage) {
          errorMessage.textContent = message;
          errorWrapper.removeAttribute('hidden');
        }
      }
    }

    handleSecondaryAddClick() {
      if (this.bundleMode === 'addons' && this.selectedAddons.length > 0) {
        this.handleAddToCartWithAddons();
      }
    }

    handleVariantChange() {
      const productInfo = document.querySelector(`#ProductInfo-${this.sectionId}`);
      if (productInfo) {
        productInfo.addEventListener('variant:change', () => {
          if (this.bundleMode === 'addons') {
            this.updateEstimatedTotal();
          } else if (this.bundleMode === 'multipack' && this.selectedMultipackQty > 1) {
            // Update bundle prices when main variant changes
            // Keep selected quantity, just update variant
            const optionIndex = this.getSelectedOptionIndex();
            if (optionIndex) {
              // Reset selected variants to use new main variant as default
              if (this.selectedBundleVariants[optionIndex]) {
                const qty = this.selectedMultipackQty;
                const newMainVariantId = this.variantInput ? parseInt(this.variantInput.value, 10) : null;
                for (let i = 1; i <= qty; i++) {
                  // Only update if variant was using the old main variant
                  if (!this.selectedBundleVariants[optionIndex][i] || 
                      this.selectedBundleVariants[optionIndex][i] === this.getCurrentVariantId(optionIndex, i)) {
                    this.selectedBundleVariants[optionIndex][i] = newMainVariantId;
                  }
                }
              }
              this.updateBundlePrice(optionIndex);
            }
            // Ensure quantity is still set correctly after variant change
            this.updateQuantity(this.selectedMultipackQty);
          }
        });
      }

      // Also listen for variant input changes
      if (this.variantInput) {
        this.variantInput.addEventListener('change', () => {
          if (this.bundleMode === 'addons') {
            this.updateEstimatedTotal();
          } else if (this.bundleMode === 'multipack' && this.selectedMultipackQty > 1) {
            // Update bundle prices when main variant changes
            const optionIndex = this.getSelectedOptionIndex();
            if (optionIndex) {
              this.updateBundlePrice(optionIndex);
            }
            // Ensure quantity is still set correctly after variant change
            this.updateQuantity(this.selectedMultipackQty);
          }
        });
      }

      // Listen for pulse:variant:change custom event if available
      document.addEventListener('pulse:variant:change', (e) => {
        if (e.detail && e.detail.sectionId === this.sectionId) {
          if (this.bundleMode === 'multipack' && this.selectedMultipackQty > 1) {
            // Keep selected quantity, just update variant
            this.updateQuantity(this.selectedMultipackQty);
            const optionIndex = this.getSelectedOptionIndex();
            if (optionIndex) {
              this.updateBundlePrice(optionIndex);
            }
          }
        }
      });
    }
  }

  // Initialize all bundle offer blocks
  function initBundleOffers() {
    const containers = document.querySelectorAll('[data-pulse-bundle]');
    containers.forEach(container => {
      if (!container.dataset.initialized) {
        container.dataset.initialized = 'true';
        new PulseBundleOffer(container);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundleOffers);
  } else {
    initBundleOffers();
  }

  // Re-initialize for dynamic content
  document.addEventListener('shopify:section:load', initBundleOffers);
})();


